import { useState } from "react";

import { useDispatch } from "react-redux";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";

import { FrmSelectFromObjects } from "components/formFields/FrmSelectFromObjects";
import { FrmResizeTextField } from "components/formFields/FrmResizeTextField";
import { ButtonProgress } from "components/utility/ButtonProgress";
import { FormErrors } from "components/utility/FormErrors";
import { VSpace } from "components/utility/VSpace";

import { openAlert } from "globalState/alertSlice";

import { contactUs } from "services/sosInventoryService/sosApi";

import { useErrors } from "hooks/useErrors";

import { initialErrorState } from "views/common";

import {
  V9_ORANGE,
  V9_ORANGE_HOVER,
  V9_ORANGE_DISABLED,
} from "views/Home/LegacyFeedbackCard";

const LEGACY_REASONS = [
  { id: "missing_feature", name: "A feature I need is missing or incomplete" },
  { id: "not_working", name: "Something isn't working correctly" },
  { id: "integration", name: "An integration isn't working as expected" },
  { id: "performance", name: "Performance or speed issues" },
  { id: "learning", name: "I need more time to learn the new version" },
  { id: "other", name: "Other" },
];

export function LegacyFeedbackDialog(props) {
  const { open, onClose } = props;

  const dispatch = useDispatch();
  const { errors, setErrors, isInError } = useErrors();
  const [record, setRecord] = useState({ category: null, details: null });
  const [loading, setLoading] = useState(false);

  function handleFieldChange(fieldName, value) {
    setRecord((prev) => ({ ...prev, [fieldName]: value }));
  }

  async function handleSubmit() {
    const validationErrors = validate(record);
    if (validationErrors.messages.length > 0) {
      setErrors(validationErrors);
      return;
    }

    const legacyWindow = window.open("", "_blank");
    setLoading(true);
    const { success, message } = await contactUs({
      subject: `V8 Return Reason: ${record.category.name}`,
      question: record.details,
    });
    setLoading(false);

    if (success) {
      legacyWindow.location.href = "https://legacy.sosinventory.com";
      dispatch(openAlert({ type: "success", message: "Thank you for your feedback" }));
      onClose();
    } else {
      legacyWindow.close();
      const errs = initialErrorState();
      errs.messages.push(message || "An error occurred. Please try again.");
      setErrors(errs);
    }
  }

  function handleClose() {
    setRecord({ category: null, details: null });
    setErrors(initialErrorState());
    onClose();
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" sx={{ inset: "0 0 10%" }}>
      <DialogTitle>Before you go, what would make V9 work for you?</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Your feedback helps us prioritize improvements. Both fields are required.
        </Typography>
        <FormErrors errors={errors} setErrors={setErrors} />
        <VSpace space={1} />
        <FrmSelectFromObjects
          name="category"
          label="Category"
          options={LEGACY_REASONS}
          onValueChange={handleFieldChange}
          value={record.category}
          error={isInError("category")}
        />
        <VSpace space={1} />
        <FrmResizeTextField
          name="details"
          label="Please tell us more"
          minRows={4}
          onValueBlur={handleFieldChange}
          value={record.details}
          error={isInError("details")}
          placeholder="What specifically do you need from the previous version? The more detail you provide, the better we can prioritize."
        />
      </DialogContent>
      <DialogActions>
        <Button color="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <div style={{ position: "relative" }}>
          <Button
            onClick={handleSubmit}
            disabled={loading}
            sx={{
              backgroundColor: V9_ORANGE,
              color: "white",
              "&:hover": { backgroundColor: V9_ORANGE_HOVER },
              "&.Mui-disabled": { backgroundColor: V9_ORANGE_DISABLED, color: "white" },
            }}
          >
            Submit &amp; go to legacy version
          </Button>
          {loading && <ButtonProgress />}
        </div>
      </DialogActions>
    </Dialog>
  );
}

function validate(record) {
  const errors = initialErrorState();
  if (!record.category) {
    errors.messages.push("Category is required.");
    errors.entity.push("category");
  }
  if (!record.details || record.details.trim() === "") {
    errors.messages.push("Details are required.");
    errors.entity.push("details");
  }
  return errors;
}
